import { useMemo, useCallback, useState, useEffect } from "react"

/**
 * Custom hook for managing MultiLine chart interactions including focused chart line state
 *
 * @param {Array} items - Current items array
 * @param {Function} setDisabledItems - Function to toggle disabled state of individual items
 * @param {Function} setOnlyItem - Function to enable only one item (disable all others)
 * @param {Function} enableItems - Function to enable specific items
 * @param {Function} setSelectedDate - Function to update selected date (optional)
 * @returns {Object} Handler functions and focused state for MultiLine interactions
 */
export function useMultiLineHandlers(items, setDisabledItems, setOnlyItem, enableItems, setSelectedDate) {
  const [focusedChartLine, setFocusedChartLine] = useState(null)

  // Clean up focused chart line when items change
  useEffect(() => {
    if (focusedChartLine && items.length > 0) {
      const focusedItemExists = items.some(item => item.id === focusedChartLine)
      if (!focusedItemExists) {
        setFocusedChartLine(null)
      }
    } else if (items.length === 0) {
      setFocusedChartLine(null)
    }
  }, [items, focusedChartLine])

  const isOnlyMode = useMemo(() => {
    if (!items || items.length === 0) return false
    const enabledItems = items.filter(item => !item.disabled)
    return enabledItems.length === 1 && items.length > 1
  }, [items])

  const onlyLegendText = isOnlyMode ? "All" : "Only"

  const allItemIds = useMemo(() => {
    return items ? items.map(item => item.id) : []
  }, [items])

  const toggle = useCallback((itemId) => {
    const currentItem = items.find(item => item.id === itemId)
    setDisabledItems(itemId, currentItem?.defaultDisabled ?? false)

    if (currentItem?.disabled) {
      setFocusedChartLine(itemId)
    } else {
      setFocusedChartLine(null)
    }
  }, [setDisabledItems, items])

  const toggleOnly = useCallback((itemId, legendText) => {
    if (isOnlyMode && legendText === "All") {
      enableItems(allItemIds)
      setFocusedChartLine(null)
    } else {
      setOnlyItem(itemId, allItemIds)
      setFocusedChartLine(itemId)
    }
  }, [setOnlyItem, enableItems, allItemIds, isOnlyMode])

  const focusChartLine = useCallback((chartLineId) => {
    setFocusedChartLine(chartLineId)
  }, [])

  const blurChartLine = useCallback(() => {
    setFocusedChartLine(null)
  }, [])

  const handleSetDate = useCallback((dateString) => {
    setSelectedDate?.(dateString)
  }, [setSelectedDate])

  const handlers = useMemo(() => ({
    toggle,
    toggleOnly,
    focusChartLine,
    blurChartLine,
    handleSetDate,
    onlyLegendText,
    isOnlyMode,
    focusedChartLine,
    setFocusedChartLine,
  }), [toggle, toggleOnly, focusChartLine, blurChartLine, handleSetDate, onlyLegendText, isOnlyMode, focusedChartLine])

  return handlers
}
